import React, {
	createContext,
	FC,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react';
import { FaUndo } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import './UndoAction.css';

export const UNDO_ACTION_DURATION_MS = 60 * 1000;

type UndoActionRequest = {
	label: string;
	onUndo: () => Promise<void> | void;
};

type ActiveUndoAction = UndoActionRequest & {
	id: number;
	expiresAt: number;
	locationIdentity: string;
};

type UndoActionContextValue = {
	showUndo: (request: UndoActionRequest) => void;
};

const UndoActionContext = createContext<UndoActionContextValue>({
	showUndo: () => undefined,
});

export const UndoActionProvider: FC = ({ children }) => {
	const location = useLocation();
	const locationIdentity = `${location.pathname}${location.search}${location.hash}`;
	const currentLocationIdentity = useRef(locationIdentity);
	currentLocationIdentity.current = locationIdentity;
	const [activeAction, setActiveAction] = useState<ActiveUndoAction | null>(null);
	const [undoing, setUndoing] = useState(false);
	const [undoError, setUndoError] = useState(false);
	const nextId = useRef(0);

	const showUndo = useCallback(
		(request: UndoActionRequest) => {
			// A request can resolve after its source screen has unmounted. Ignore that stale action.
			if (currentLocationIdentity.current !== locationIdentity) return;
			nextId.current += 1;
			setUndoing(false);
			setUndoError(false);
			setActiveAction({
				...request,
				id: nextId.current,
				expiresAt: Date.now() + UNDO_ACTION_DURATION_MS,
				locationIdentity,
			});
		},
		[locationIdentity],
	);

	useEffect(() => {
		setActiveAction((current) => (current?.locationIdentity === locationIdentity ? current : null));
		setUndoing(false);
		setUndoError(false);
	}, [locationIdentity]);

	useEffect(() => {
		if (!activeAction) return undefined;
		const remaining = Math.max(0, activeAction.expiresAt - Date.now());
		const timer = window.setTimeout(() => {
			setActiveAction((current) => (current?.id === activeAction.id ? null : current));
		}, remaining);
		return () => window.clearTimeout(timer);
	}, [activeAction]);

	const undo = async () => {
		if (!activeAction || undoing) return;
		const action = activeAction;
		setUndoing(true);
		setUndoError(false);
		try {
			await action.onUndo();
			setActiveAction((current) => (current?.id === action.id ? null : current));
		} catch (_error) {
			setUndoError(true);
		} finally {
			setUndoing(false);
		}
	};

	return (
		<UndoActionContext.Provider value={{ showUndo }}>
			{children}
			{activeAction && (
				<div className="undo-action" role="status" aria-live="polite">
					<span className="undo-action__message">
						{undoError ? 'Undo failed. Try again.' : activeAction.label}
					</span>
					<button type="button" className="undo-action__button" disabled={undoing} onClick={undo}>
						<FaUndo aria-hidden="true" />
						{undoing ? 'Restoring…' : 'Undo'}
					</button>
				</div>
			)}
		</UndoActionContext.Provider>
	);
};

export const useUndoAction = () => useContext(UndoActionContext);
